import { MouseEvent } from 'react';
import { css } from '@emotion/css';
import { make as RichText } from '@prezzee/ribbon/src/components/personal/RichText/RichText.gen';
import { CustomerCareCallUs } from 'api/graphql/generated';
import { useRouter } from 'next/router';

import { Color } from '../../../css/theme';
import Tag, { linkType } from './Tag';
import useSegment from 'hooks/useSegment';
import useSmartLink from 'hooks/useSmartLink';
import { SegmentUtils } from 'utils/SegmentUtils.bs';

interface Props {
  contactData: CustomerCareCallUs;
}

const Styles = {
  container: css`
    display: flex;
    flex-direction: column;
    padding-bottom: 0.5rem;
    background-color: ${Color.White};
  `,
  title: css`
    font-size: 1.375rem;
    font-weight: bold;
    color: ${Color.Gray800};
    margin-bottom: 1.7rem;
  `,
  description: css`
    font-size: 1.125rem;
    color: ${Color.Gray600};
    margin-bottom: 0.7rem;

    @media (max-width: 576px) {
      margin-bottom: 0.4rem;
    }
  `,
  additionalDescription: css`
    font-size: 1.125rem;
    color: ${Color.Gray600};
    margin-top: 1.3rem;

    @media (max-width: 576px) {
      margin-top: 0.8rem;
    }
  `,
  phoneEmailTagContainer: css`
    display: flex;
    flex-direction: row;
    margin-bottom: 0.7rem;
    flex-wrap: wrap;

    @media (max-width: 576px) {
      flex-direction: column;
      margin-bottom: 0.4rem;
    }
  `,
  internationalCallText: css`
    font-size: 1.2rem;
    font-weight: bold;
    color: ${Color.Gray800};
    margin-bottom: 0.5rem;
  `,
};

const ContactPhoneEmail = ({ contactData }: Props) => {
  let {
    title,
    text,
    phoneContactMethodIconAsset,
    formContactMethodUrl,
    formContactMethodIconAsset,
    formContactMethodLabel,
    localPhoneNumber,
    internationalPhoneNumber,
    internationalPhoneNumberText,
    additionalContactMethodIconAsset,
    additionalContactMethodCopy,
    additionalContactMethodUrl,
    additionalContactMethodLabel,
    _system_,
  } = contactData;
  const { asPath } = useRouter();
  const { customElementInteraction, customElementInteractionAsync } = useSegment();
  const spotlightProps = useSmartLink(_system_);

  const isAdditionalContactMethod = additionalContactMethodLabel !== '';

  return (
    <div className={Styles.container} {...spotlightProps}>
      <h3 className={Styles.title}>{title}</h3>
      <RichText className={Styles.description}>{text.html}</RichText>
      <div className={Styles.phoneEmailTagContainer}>
        <Tag
          href={formContactMethodUrl}
          icon={formContactMethodIconAsset}
          type={linkType.website}
          onClick={(e: MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();

            customElementInteractionAsync({
              element: SegmentUtils.mapElement(formContactMethodLabel),
              path: asPath,
            }).finally(() => window.open(formContactMethodUrl, '_self'));
          }}
        >
          {formContactMethodLabel}
        </Tag>
        <Tag
          onClick={() => {
            customElementInteraction({
              element: 'call_national',
              path: asPath,
            });
          }}
          icon={phoneContactMethodIconAsset}
          type={linkType.phone}
        >
          {localPhoneNumber}
        </Tag>
      </div>
      <h5 className={Styles.internationalCallText}>{internationalPhoneNumberText}</h5>
      <Tag
        onClick={() => {
          customElementInteraction({
            element: 'call_international',
            path: asPath,
          });
        }}
        icon={phoneContactMethodIconAsset}
        type={linkType.phone}
      >
        {internationalPhoneNumber}
      </Tag>
      {isAdditionalContactMethod ? (
        <>
          <RichText className={Styles.additionalDescription}>{additionalContactMethodCopy.html}</RichText>
          <Tag
            icon={additionalContactMethodIconAsset}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();

              customElementInteractionAsync({
                element: 'lodge_a_complaint_cta',
                path: asPath,
              }).finally(() => window.open(additionalContactMethodUrl, '_self'));
            }}
            type={linkType.website}
            href={additionalContactMethodUrl}
          >
            {additionalContactMethodLabel}
          </Tag>
        </>
      ) : null}
    </div>
  );
};

export default ContactPhoneEmail;
